"use client";

import { useMemo, useState } from "react";
import {
  Button,
  Icon,
  IconButton,
  Input,
  Label,
  Plus,
  Popover,
  SegmentedControl,
  Select,
  Swatch,
  Toggle,
  Tooltip,
  Trash2,
  cx,
} from "@/components";
import {
  CLUSTER_LEGIBILITY_THRESHOLD,
  categoryColoring,
  getMapTokens,
  shapeAssignment,
  type FilterClause,
  type FilterOp,
  type MapSpec,
  type MarkerShape,
  type MarkerSize,
  type PointColumnType,
  type PointSet,
} from "@/lib/map";
import { filteredCount } from "./mapLayers";
import { SHAPE_ICONS } from "./shapeGlyph";

/**
 * The marker shape palette (FAIL-397). Pin uses the dotted map-pin glyph and
 * pin-solid the plain one, matching how they render; the rest are the SDF shapes.
 * Glyphs come from the shared `SHAPE_ICONS` map so the picker, the shape-by
 * preview, and the legend all name a shape with the SAME icon.
 */
const SHAPE_OPTIONS: Array<{ shape: MarkerShape; label: string }> = [
  { shape: "pin", label: "Pin" },
  { shape: "pin-solid", label: "Pin, solid" },
  { shape: "circle", label: "Circle" },
  { shape: "square", label: "Square" },
  { shape: "diamond", label: "Diamond" },
  { shape: "triangle", label: "Triangle" },
  { shape: "star", label: "Star" },
  { shape: "hexagon", label: "Hexagon" },
];

export interface StylePanelProps {
  pointSet: PointSet;
  spec: MapSpec;
  themeName: string;
  /** Unique per rendered instance (side panel vs drawer) so ids never collide. */
  idPrefix: string;
  onSpecChange: (next: MapSpec) => void;
}

const NUMERIC_TYPES: PointColumnType[] = ["number"];

function isNumericColumn(type: PointColumnType | undefined): boolean {
  return type !== undefined && NUMERIC_TYPES.includes(type);
}

/** Operators offered per column type (categorical / numeric / text + the any-ops). */
function opsForType(type: PointColumnType | undefined): { value: FilterOp; label: string }[] {
  if (isNumericColumn(type)) {
    return [
      { value: "gte", label: "at least" },
      { value: "lte", label: "at most" },
      { value: "between", label: "between" },
      { value: "empty", label: "is empty" },
      { value: "not_empty", label: "is not empty" },
    ];
  }
  return [
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
    { value: "in", label: "is any of" },
    { value: "contains", label: "contains" },
    { value: "empty", label: "is empty" },
    { value: "not_empty", label: "is not empty" },
  ];
}

function defaultClause(pointSet: PointSet): FilterClause {
  const col = pointSet.columns[0];
  const numeric = isNumericColumn(col?.type);
  return { column: col?.name ?? "", op: numeric ? "gte" : "is", value: numeric ? 0 : "" };
}

/** Distinct values of a column across the point set (capped for the pickers). */
function distinctValues(pointSet: PointSet, column: string, cap = 40): string[] | null {
  const seen = new Set<string>();
  for (const p of pointSet.points) {
    const v = p.props[column] ?? "";
    if (v.trim() === "") continue;
    seen.add(v);
    if (seen.size > cap) return null;
  }
  return [...seen].sort();
}

/**
 * Style panel — the working-register controls beside the map hero. Every edit
 * writes a new MapSpec; the canvas diffs it onto the live style (< 100ms
 * perceived, no reload). Composes ONLY from the M1 primitives.
 */
export function StylePanel({ pointSet, spec, themeName, idPrefix, onSpecChange }: StylePanelProps) {
  const tokens = useMemo(() => getMapTokens(themeName), [themeName]);
  const [swatchPickerFor, setSwatchPickerFor] = useState<string | null>(null);
  /** Raw in-progress text for "is any of" inputs, keyed by clause index. */
  const [inDrafts, setInDrafts] = useState<Record<number, string>>({});

  const columnType = (name: string | undefined): PointColumnType | undefined =>
    pointSet.columns.find((c) => c.name === name)?.type;

  const numericColumns = pointSet.columns.filter((c) => isNumericColumn(c.type));
  const colorBy = spec.encoding.colorBy;

  const coloring = useMemo(() => {
    if (!colorBy || colorBy.mode !== "categorical") return null;
    const values = pointSet.points.map((p) => p.props[colorBy.column] ?? "");
    return categoryColoring(values, tokens.palette, tokens.paletteOther, colorBy.overrides);
  }, [pointSet, colorBy, tokens]);

  // MAP-24 shape-by: the deterministic value→shape assignment for the picked column
  // (drives the preview beneath the select; the canvas + legend derive the same).
  const iconBy = spec.encoding.iconBy;
  const shaping = useMemo(() => {
    if (!iconBy) return null;
    const values = pointSet.points.map((p) => p.props[iconBy.column] ?? "");
    return shapeAssignment(values);
  }, [pointSet, iconBy]);

  const showing = useMemo(() => filteredCount(pointSet, spec.filters), [pointSet, spec.filters]);

  function update(patch: Partial<MapSpec>) {
    onSpecChange({ ...spec, ...patch });
  }

  function setColorColumn(column: string) {
    if (column === "") {
      update({ encoding: { ...spec.encoding, colorBy: undefined } });
      return;
    }
    const mode = isNumericColumn(columnType(column)) ? "numeric" : "categorical";
    update({ encoding: { ...spec.encoding, colorBy: { column, mode } } });
  }

  function setColorMode(mode: "categorical" | "numeric") {
    if (!colorBy) return;
    update({ encoding: { ...spec.encoding, colorBy: { ...colorBy, mode, overrides: undefined } } });
  }

  function setOverride(value: string, hex: string | null) {
    if (!colorBy) return;
    const overrides = { ...(colorBy.overrides ?? {}) };
    if (hex === null) delete overrides[value];
    else overrides[value] = hex;
    update({
      encoding: {
        ...spec.encoding,
        colorBy: { ...colorBy, overrides: Object.keys(overrides).length > 0 ? overrides : undefined },
      },
    });
    setSwatchPickerFor(null);
  }

  function setSizeColumn(column: string) {
    update({
      encoding: { ...spec.encoding, sizeBy: column === "" ? undefined : { column } },
    });
  }

  function setShapeColumn(column: string) {
    update({
      encoding: { ...spec.encoding, iconBy: column === "" ? undefined : { column } },
    });
  }

  // Marker shape/size (FAIL-476). Absent `marker` = the classic circle, so a
  // grandfathered map shows "Circle" selected; touching either control makes the
  // choice explicit on the spec.
  const marker = spec.marker ?? { shape: "circle" as MarkerShape, size: "m" as MarkerSize };
  function setMarkerShape(shape: MarkerShape) {
    update({ marker: { shape, size: marker.size } });
  }
  function setMarkerSize(size: MarkerSize) {
    update({ marker: { shape: marker.shape, size } });
  }

  function setFilters(filters: FilterClause[]) {
    update({ filters });
  }

  function patchClause(index: number, patch: Partial<FilterClause>) {
    const next = spec.filters.map((clause, i) => (i === index ? { ...clause, ...patch } : clause));
    setFilters(next);
  }

  function changeClauseColumn(index: number, column: string) {
    const ops = opsForType(columnType(column));
    const clause = spec.filters[index]!;
    const keepOp = ops.some((o) => o.value === clause.op);
    const op = keepOp ? clause.op : ops[0]!.value;
    const value: FilterClause["value"] =
      op === "between" ? [0, 0] : op === "in" ? [] : isNumericColumn(columnType(column)) ? 0 : "";
    setInDrafts((d) => ({ ...d, [index]: "" }));
    patchClause(index, { column, op, value });
  }

  function changeClauseOp(index: number, op: FilterOp) {
    const clause = spec.filters[index]!;
    const numeric = isNumericColumn(columnType(clause.column));
    const value: FilterClause["value"] =
      op === "between" ? [0, 0] : op === "in" ? [] : op === "empty" || op === "not_empty" ? null : numeric ? 0 : "";
    setInDrafts((d) => ({ ...d, [index]: "" }));
    patchClause(index, { op, value });
  }

  function clauseValueInput(clause: FilterClause, index: number) {
    const type = columnType(clause.column);
    switch (clause.op) {
      case "empty":
      case "not_empty":
        return null;
      case "gte":
      case "lte":
        return (
          <Input
            type="number"
            aria-label="Filter value"
            value={String(clause.value ?? "")}
            onChange={(e) => patchClause(index, { value: Number(e.target.value) })}
          />
        );
      case "between": {
        const [a, b] = Array.isArray(clause.value) ? clause.value : [0, 0];
        return (
          <span className="style-filter-clause__pair">
            <Input
              type="number"
              aria-label="Minimum"
              value={String(a ?? "")}
              onChange={(e) => patchClause(index, { value: [Number(e.target.value), Number(b ?? 0)] })}
            />
            <Input
              type="number"
              aria-label="Maximum"
              value={String(b ?? "")}
              onChange={(e) => patchClause(index, { value: [Number(a ?? 0), Number(e.target.value)] })}
            />
          </span>
        );
      }
      case "in": {
        const draft = inDrafts[index] ?? (Array.isArray(clause.value) ? (clause.value as string[]).join(", ") : "");
        return (
          <Input
            aria-label="Comma-separated values"
            placeholder="value, value, …"
            value={draft}
            onChange={(e) => {
              const text = e.target.value;
              setInDrafts((d) => ({ ...d, [index]: text }));
              patchClause(index, {
                value: text.split(",").map((v) => v.trim()).filter((v) => v !== ""),
              });
            }}
          />
        );
      }
      case "is":
      case "is_not": {
        const options = type === "number" ? null : distinctValues(pointSet, clause.column);
        if (options && options.length > 0) {
          return (
            <Select
              aria-label="Filter value"
              value={String(clause.value ?? "")}
              onChange={(e) => patchClause(index, { value: e.target.value })}
            >
              <option value="">— pick a value —</option>
              {options.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
          );
        }
        return (
          <Input
            aria-label="Filter value"
            value={String(clause.value ?? "")}
            onChange={(e) => patchClause(index, { value: e.target.value })}
          />
        );
      }
      default:
        return (
          <Input
            aria-label="Filter value"
            value={String(clause.value ?? "")}
            onChange={(e) => patchClause(index, { value: e.target.value })}
          />
        );
    }
  }

  const colorSelectId = `${idPrefix}-style-color`;
  const sizeSelectId = `${idPrefix}-style-size`;
  const shapeSelectId = `${idPrefix}-style-shape`;
  const weightSelectId = `${idPrefix}-style-weight`;

  return (
    <div className="style-panel__body">
      {/* ------------------------------------------------ Color by */}
      <div className="style-panel__row">
        <Label htmlFor={colorSelectId}>Color by</Label>
        <Select id={colorSelectId} value={colorBy?.column ?? ""} onChange={(e) => setColorColumn(e.target.value)}>
          <option value="">— single color —</option>
          {pointSet.columns.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </Select>
        {colorBy && isNumericColumn(columnType(colorBy.column)) && (
          <SegmentedControl<"categorical" | "numeric">
            label="Color scale"
            options={[
              { value: "categorical", label: "Categories" },
              { value: "numeric", label: "Scale" },
            ]}
            value={colorBy.mode}
            onChange={setColorMode}
          />
        )}
      </div>

      {coloring && coloring.entries.length > 0 && (
        <div className="style-swatches">
          {coloring.entries
            .filter((entry) => !entry.isOther)
            .map((entry) => (
              <span key={entry.value} className="style-swatch-row">
                <Popover
                  open={swatchPickerFor === entry.value}
                  trigger={
                    <button
                      type="button"
                      className="style-swatch-btn"
                      aria-label={`Change color for ${entry.value}`}
                      onClick={() => setSwatchPickerFor(swatchPickerFor === entry.value ? null : entry.value)}
                    >
                      <Swatch color={entry.color} />
                    </button>
                  }
                >
                  <span className="style-swatch-pick">
                    {tokens.palette.map((hex, i) => (
                      <button
                        key={hex}
                        type="button"
                        className="style-swatch-btn"
                        aria-label={`Palette color ${i + 1}`}
                        onClick={() => setOverride(entry.value, hex)}
                      >
                        <Swatch color={hex} />
                      </button>
                    ))}
                    <span className="style-swatch-pick__reset">
                      <Button variant="ghost" size="sm" onClick={() => setOverride(entry.value, null)}>
                        Auto
                      </Button>
                    </span>
                  </span>
                </Popover>
                <span className="style-swatch-row__label">{entry.value}</span>
              </span>
            ))}
          {coloring.hasOther && (
            <span className="style-swatch-row">
              <span className="style-swatch-btn" aria-hidden>
                <Swatch color={coloring.other} />
              </span>
              <span className="style-swatch-row__label style-swatch-row__label--other">Other</span>
            </span>
          )}
        </div>
      )}

      {/* ------------------------------------------------- Size by */}
      <div className="style-panel__row">
        <Label htmlFor={sizeSelectId}>Size by</Label>
        <Select
          id={sizeSelectId}
          value={spec.encoding.sizeBy?.column ?? ""}
          onChange={(e) => setSizeColumn(e.target.value)}
          disabled={numericColumns.length === 0}
        >
          <option value="">— uniform —</option>
          {numericColumns.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </Select>
        {numericColumns.length === 0 && (
          <p className="style-panel__hint">Add a numeric column to size points by value.</p>
        )}
      </div>

      {/* ------------------------------------------------ Shape by */}
      <div className="style-panel__row">
        <Label htmlFor={shapeSelectId}>Shape by</Label>
        <Select
          id={shapeSelectId}
          value={iconBy?.column ?? ""}
          onChange={(e) => setShapeColumn(e.target.value)}
        >
          <option value="">— single shape —</option>
          {pointSet.columns.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </Select>
        {shaping && shaping.entries.length > 0 && (
          <div className="style-shapes">
            {shaping.entries.map((entry) => (
              <span key={entry.value} className="style-shape-row">
                <span className="style-shape-glyph" aria-hidden>
                  <Icon icon={SHAPE_ICONS[entry.shape]} size="sm" />
                </span>
                <span
                  className={cx("style-shape-row__label", entry.isOther && "style-shape-row__label--other")}
                >
                  {entry.value}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ------------------------------------------------- Markers */}
      <div className="style-panel__row">
        <Label>Marker</Label>
        <div className="style-markers" role="radiogroup" aria-label="Marker shape">
          {SHAPE_OPTIONS.map(({ shape, label }) => (
            <Tooltip key={shape} content={label}>
              <button
                type="button"
                role="radio"
                aria-checked={marker.shape === shape}
                aria-label={label}
                className={cx("style-marker", marker.shape === shape && "style-marker--active")}
                onClick={() => setMarkerShape(shape)}
              >
                <Icon icon={SHAPE_ICONS[shape]} size="md" />
              </button>
            </Tooltip>
          ))}
        </div>
        <SegmentedControl<MarkerSize>
          label="Marker size"
          options={[
            { value: "s", label: "S" },
            { value: "m", label: "M" },
            { value: "l", label: "L" },
          ]}
          value={marker.size}
          onChange={setMarkerSize}
        />
      </div>

      <hr className="style-panel__divider" />

      {/* Basemap moved to its own "Maps" tab (MAP-102 follow-up) — see BasemapPanel. */}

      {/* -------------------------------------- Clustering + heat */}
      <div className="style-panel__row">
        <span className="style-panel__inline">
          <Label>Cluster nearby points</Label>
          <Toggle
            aria-label="Cluster nearby points"
            checked={spec.clustering.enabled}
            onChange={(e) => update({ clustering: { ...spec.clustering, enabled: e.target.checked } })}
          />
        </span>
        {!spec.clustering.enabled && pointSet.count > CLUSTER_LEGIBILITY_THRESHOLD && (
          <p className="style-panel__warn">
            {pointSet.count.toLocaleString()} points without clustering can get crowded and slow.
          </p>
        )}
      </div>

      <div className="style-panel__row">
        <span className="style-panel__inline">
          <Label>Heat map</Label>
          <Toggle
            aria-label="Heat map"
            checked={spec.heat.enabled}
            onChange={(e) => update({ heat: { ...spec.heat, enabled: e.target.checked } })}
          />
        </span>
        {spec.heat.enabled && (
          <>
            <Label htmlFor={weightSelectId}>Weight by</Label>
            <Select
              id={weightSelectId}
              value={spec.heat.weightColumn ?? ""}
              onChange={(e) =>
                update({ heat: { ...spec.heat, weightColumn: e.target.value === "" ? undefined : e.target.value } })
              }
              disabled={numericColumns.length === 0}
            >
              <option value="">— each point equal —</option>
              {numericColumns.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </Select>
          </>
        )}
      </div>

      <div className="style-panel__row">
        <span className="style-panel__inline">
          <Label>Legend</Label>
          <Toggle
            aria-label="Show legend"
            checked={spec.legend.enabled}
            onChange={(e) => update({ legend: { ...spec.legend, enabled: e.target.checked } })}
          />
        </span>
      </div>

      <hr className="style-panel__divider" />

      {/* ------------------------------------------------- Filters */}
      <div className="style-filters">
        <span className="style-panel__inline">
          <Label>Filters</Label>
          <Button
            variant="secondary"
            size="sm"
            iconLeading={<Icon icon={Plus} size="sm" />}
            onClick={() => setFilters([...spec.filters, defaultClause(pointSet)])}
          >
            Add filter
          </Button>
        </span>

        {spec.filters.length > 0 && (
          <p className="style-filter-count" role="status">
            Showing {showing.toLocaleString()} of {pointSet.count.toLocaleString()} points
          </p>
        )}

        {spec.filters.map((clause, index) => (
          <span key={index} className="style-filter-clause">
            <span className="style-filter-clause__fields">
              <Select
                aria-label="Filter column"
                value={clause.column}
                onChange={(e) => changeClauseColumn(index, e.target.value)}
              >
                {pointSet.columns.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Filter operator"
                value={clause.op}
                onChange={(e) => changeClauseOp(index, e.target.value as FilterOp)}
              >
                {opsForType(columnType(clause.column)).map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </Select>
              {clauseValueInput(clause, index)}
            </span>
            <IconButton
              label="Remove filter"
              variant="ghost"
              onClick={() => {
                setInDrafts((d) => {
                  const next: Record<number, string> = {};
                  Object.entries(d).forEach(([k, v]) => {
                    const i = Number(k);
                    if (i < index) next[i] = v;
                    else if (i > index) next[i - 1] = v;
                  });
                  return next;
                });
                setFilters(spec.filters.filter((_, i) => i !== index));
              }}
            >
              <Icon icon={Trash2} size="sm" />
            </IconButton>
          </span>
        ))}
      </div>
    </div>
  );
}
